import { type QueryClient, type Query } from "@tanstack/react-query";
import { type AssignmentScope } from "../../new-rates-and-dosage/services/seedAssignments";

/** Refresh only reads affected by this Design transition, including inactive caches. */
export const prepareRatesAndDosage = async (
  client: QueryClient,
  trialId: string,
  scope: AssignmentScope,
  isStrip: boolean
): Promise<void> => {
  const isSharedTrial = (query: Query): boolean =>
    query.queryKey.length === 2 &&
    query.queryKey[0] === "trial" &&
    query.queryKey[1] === trialId;
  const filters = {
    predicate: (query: Query): boolean =>
      isSharedTrial(query) ||
      ((query.queryKey[0] === "seedAssignmentsTrial" ||
        query.queryKey[0] === "stripSeedAssignmentsTrial") &&
        query.queryKey[1] === scope.orgId &&
        query.queryKey[2] === scope.workspaceId &&
        query.queryKey[3] === trialId),
  };
  const seedKey = isStrip
    ? "stripSeedAssignmentsTrial"
    : "seedAssignmentsTrial";
  const reads = client
    .getQueryCache()
    .findAll(filters)
    .filter((query) => isSharedTrial(query) || query.queryKey[0] === seedKey);

  // Cancel first: a pre-save response must not repopulate these caches later.
  await client.cancelQueries(filters);
  await client.invalidateQueries({ ...filters, refetchType: "none" });
  // fetchQuery refreshes inactive reads and propagates failures. Merely
  // invalidating (or prefetching, which swallows errors) is not readiness.
  await Promise.all(
    reads.map(
      async (query) =>
        await client.fetchQuery({
          ...query.options,
          queryKey: query.queryKey,
          staleTime: 0,
          retry: false,
        })
    )
  );
  // Cold Seeds caches remain cold; the destination's independent bootstrap
  // loads them normally, with no older response or warm design to reuse.
};
